import { Component, ReactNode, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { ErrorFallback } from "./ErrorFallback/ErrorFallback";
import MxLoading from "@rf-l1/loading/MxLoading";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  suspendComponent?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class MxErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Obtener información almacenada
    setTimeout(() => {
      const baseInfo = sessionStorage.getItem("baseErrorInfo");
      const formStates = Object.entries(sessionStorage)
        .filter(([key]) => key.startsWith("formState_"))
        .map(([, value]) => JSON.parse(value));
      const listStates = Object.entries(sessionStorage)
        .filter(([key]) => key.startsWith("listState_"))
        .map(([, value]) => JSON.parse(value));
      const detailStates = Object.entries(sessionStorage)
        .filter(([key]) => key.startsWith("detailState_"))
        .map(([, value]) => JSON.parse(value));

      Sentry.withScope(scope => {
        // Información base
        const parsedBaseInfo = baseInfo ? JSON.parse(baseInfo) : {};
        scope.setUser({
          id: parsedBaseInfo.userData?.id,
          email: parsedBaseInfo.userData?.email
        });
        scope.setTag("version", parsedBaseInfo.version);
        scope.setTag("route", parsedBaseInfo.url);

        scope.setExtras({
          formStates: JSON.stringify(formStates),
          listStates: JSON.stringify(listStates),
          detailStates: JSON.stringify(detailStates),
          componentStack: JSON.stringify(errorInfo.componentStack)
        });
        const id = Sentry.captureException(error);
      });
    }, 200);
  }

  render() {
    const {
      children,
      fallback,
      suspendComponent = <MxLoading variant="gradient" type="full screen" />
    } = this.props;

    if (this.state.hasError) {
      return (
        fallback || (
          <ErrorFallback
            error={this.state.error}
            resetErrorBoundary={() => this.setState({ hasError: false })}
          />
        )
      );
    }

    return <Suspense fallback={suspendComponent}>{children}</Suspense>;
  }
}

export default MxErrorBoundary;
